import {
    initialPortfolioSummaryAggregationBySide,
    PortfolioSummaryAggregatedData,
    PortfolioSummaryAggregationItem,
} from '../store/data/PortfolioSummaryDataState';
import {
    SidesGroupName,
    NetGroupName,
    TotalGroupName,
    BuyGroupName,
    SellGroupName,
    ShortGroupName,
    PortfolioSummarySortableDataItem,
} from 'widgets/portfolioSummary/aggregation/strategies/AggregationStrategy';
import { textUtils } from '@instinet-analytics/core-lib';
import { getAggregationStrategy } from 'widgets/portfolioSummary/aggregation/strategies/AggregationStrategyFactory';
import { AggregationField } from 'widgets/portfolioSummary/aggregation/fields/aggregationFieldPortfolio';

const { isFalsyOrWhitespace } = textUtils;

export interface PortfolioSummaryDataByGroupItem
    extends PortfolioSummaryAggregationItem,
        PortfolioSummarySortableDataItem {
    index: string;
    groupName: string;
    orderedValueLoc: number;
    tradedValueLoc: number;
    leavesValueLoc: number;
    netLeavesValueLoc: number;
    netOrderedValueLoc: number;
    netTradedValueLoc: number;
    averagePercentageCompletion: number | undefined;
}

interface GetPortfolioSummaryDataByGroupOptions {
    showBuysAsPositive: boolean;
    combineSellsAndShorts: boolean;
    addTotalGroup: boolean;
    exchangeRate: number;
}

export function getPortfolioSummaryDataByGroup(
    aggregationField: AggregationField,
    aggregatedData: PortfolioSummaryAggregatedData,
    options: GetPortfolioSummaryDataByGroupOptions
): PortfolioSummaryDataByGroupItem[] {
    const {
        combineSellsAndShorts,
        showBuysAsPositive,
        exchangeRate,
        addTotalGroup,
    } = options;
    const aggregationStrategy = getAggregationStrategy(
        aggregationField,
        combineSellsAndShorts
    );

    if (aggregationField === 'side') {
        return collateSideData(
            exchangeRate,
            aggregatedData,
            showBuysAsPositive
        );
    }

    const ret: PortfolioSummaryDataByGroupItem[] = [];
    let index = 0;
    const total = toItem(
        TotalGroupName,
        index++,
        exchangeRate,
        initialPortfolioSummaryAggregationBySide,
        showBuysAsPositive,
        initialPortfolioSummaryAggregationBySide
    );
    total.selectionDisabled = true;
    if (addTotalGroup) {
        ret.push(total);
    }
    for (const groupName of Object.keys(aggregatedData)) {
        const group = aggregatedData[groupName];

        if (group.total) {
            const item = toItem(
                groupName,
                index++,
                exchangeRate,
                group.total,
                showBuysAsPositive,
                group.net
            );
            addTotal(total, item);
            ret.push(item);
        }
    }

    total.averagePercentageCompletion =
        (total.tradedValue / total.orderedValue) * 100;

    return ret.sort((x, y) => aggregationStrategy.sort(x, y));
}

export function collateSideData(
    exchangeRate: number,
    aggregatedData: PortfolioSummaryAggregatedData,
    showBuysAsPositive: boolean
): PortfolioSummaryDataByGroupItem[] {
    if (!aggregatedData[SidesGroupName]) {
        return [];
    }
    const ret: PortfolioSummaryDataByGroupItem[] = [];
    ret.push(
        toItem(
            TotalGroupName,
            1,
            exchangeRate,
            aggregatedData[SidesGroupName].total,
            showBuysAsPositive
        )
    );

    const net = toItem(
        NetGroupName,
        2,
        exchangeRate,
        aggregatedData[SidesGroupName].net,
        showBuysAsPositive
    );

    if (!showBuysAsPositive) {
        invert(net);
    }

    net.averagePercentageCompletion = undefined;
    ret.push(net);

    ret.push(
        toItem(
            BuyGroupName,
            3,
            exchangeRate,
            aggregatedData[SidesGroupName].buy,
            showBuysAsPositive
        )
    );
    ret.push(
        toItem(
            SellGroupName,
            4,
            exchangeRate,
            aggregatedData[SidesGroupName].sell,
            !showBuysAsPositive // Invert net back again so sells are offset from buys in the net calculation
        )
    );
    aggregatedData[SidesGroupName].short &&
        ret.push(
            toItem(
                ShortGroupName,
                5,
                exchangeRate,
                aggregatedData[SidesGroupName].short!,
                !showBuysAsPositive // Invert net back again so shorts are offset from buys in the net calculation
            )
        );
    return ret;
}

function toItem(
    groupName: string,
    index: number,
    exchangeRate: number,
    totalAggregation: PortfolioSummaryAggregationItem,
    showBuysAsPositive: boolean,
    netAggregation?: PortfolioSummaryAggregationItem
): PortfolioSummaryDataByGroupItem {
    const netMultiplier = showBuysAsPositive ? 1 : -1;

    const res: PortfolioSummaryDataByGroupItem = {
        index: index.toString(),
        groupName: isFalsyOrWhitespace(groupName) ? 'N/A' : groupName,
        ...totalAggregation,
        orderedValueLoc: totalAggregation.orderedValue * exchangeRate,
        tradedValueLoc: totalAggregation.tradedValue * exchangeRate,
        leavesValueLoc: totalAggregation.leavesValue * exchangeRate,
        netLeavesValueLoc:
            (netAggregation?.leavesValue ?? totalAggregation.leavesValue) *
            exchangeRate *
            netMultiplier,
        netTradedValueLoc:
            (netAggregation?.tradedValue ?? totalAggregation.tradedValue) *
            exchangeRate *
            netMultiplier,
        netOrderedValueLoc:
            (netAggregation?.orderedValue ?? totalAggregation.orderedValue) *
            exchangeRate *
            netMultiplier,
        averagePercentageCompletion: undefined,
    };

    if (totalAggregation.orderedValue) {
        res.averagePercentageCompletion =
            (totalAggregation.tradedValue / totalAggregation.orderedValue) *
            100;
    }

    return res;
}

function invert(item: PortfolioSummaryDataByGroupItem) {
    item.count = item.count * -1;
    item.orderQty = item.orderQty * -1;
    item.tradedQty = item.tradedQty * -1;
    item.leavesQty = item.leavesQty * -1;
    item.orderedValue = item.orderedValue * -1;
    item.orderedValueLoc = item.orderedValueLoc * -1;
    item.leavesValue = item.leavesValue * -1;
    item.leavesValueLoc = item.leavesValueLoc * -1;
    item.tradedValue = item.tradedValue * -1;
    item.tradedValueLoc = item.tradedValueLoc * -1;
}

function addTotal(
    total: PortfolioSummaryDataByGroupItem,
    item: PortfolioSummaryDataByGroupItem
) {
    total.count += item.count;
    total.orderQty += item.orderQty;
    total.tradedQty += item.tradedQty;
    total.leavesQty += item.leavesQty;
    total.orderedValue += item.orderedValue;
    total.orderedValueLoc += item.orderedValueLoc;
    total.leavesValue += item.leavesValue;
    total.leavesValueLoc += item.leavesValueLoc;
    total.tradedValue += item.tradedValue;
    total.tradedValueLoc += item.tradedValueLoc;

    total.netOrderedValueLoc += item.netOrderedValueLoc;
    total.netTradedValueLoc += item.netTradedValueLoc;
    total.netLeavesValueLoc += item.netLeavesValueLoc;
}
